import React, { useRef, useEffect } from 'react';
import { TextField, Text, ShimmerElementType, Shimmer } from '@fluentui/react';
import './SearchMSX.scss';
import { useGetCustomersList } from 'app/services/search-api';
import { MsxSuggestDocument, MsxSuggestResponse } from 'generated/search-backend';
import { Trans } from 'react-i18next';
import { keyPress } from 'app/components/helpers';
interface IProps {
    oppId:string;
    setOppId: Function;
    searchValue: string;
    setSearchValue: Function;
    setSuggestionSelected: Function;
    setFilteredData: Function;
    setRevenueRisk: Function;
    setImpDevices: Function;
    setRevenueHText: Function;
    setImpDevicesHText: Function;
    autoSuggestHText: boolean;
    setAutoSuggestHText: Function;
    origin: string;
    isFocus?: boolean;
    setCustomerSelected?: Function;
    setLastSelectedOppurtunityId?: Function;
    setOriginalSelected?: Function;
}
export const SearchMSX: React.FC<IProps> = ({
    searchValue,
    setSearchValue,
    setSuggestionSelected,
    setFilteredData,
    setRevenueRisk,
    setImpDevices,
    setRevenueHText,
    setImpDevicesHText,
    autoSuggestHText,
    setAutoSuggestHText,
    origin,
    oppId,
    setOppId,
    isFocus,
    setCustomerSelected,
    setLastSelectedOppurtunityId,
    setOriginalSelected
}) => {
    const [showSuggestion, setShowSuggestion] = React.useState(false);

    let count = 20;

    if (origin === 'REQUEST_EXPERIENCE') {
        count = 50;
    }

    const { data, mutate: getCustomersList, isLoading } = useGetCustomersList();

    const handleSearch = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (origin === 'ADD_CUSTOMER') {
            setCustomerSelected(false);
            setOriginalSelected([]);
            setFilteredData([]);
        }

        setAutoSuggestHText(false);

        setSearchValue((e.target as HTMLInputElement | HTMLTextAreaElement).value);
        if ((e.target as HTMLInputElement | HTMLTextAreaElement).value.length > 2) {
            setShowSuggestion(true);
            getCustomersList({searchValue: (e.target as HTMLInputElement | HTMLTextAreaElement).value});
        } else {
            setShowSuggestion(false);
        }
    };

    const handleSelectSuggestion = (selectedData: MsxSuggestDocument) => {
        if (origin === 'ADD_CUSTOMER') {
            setLastSelectedOppurtunityId(selectedData.opportunityNumber);
            setCustomerSelected(true);
            setOriginalSelected([{ ...selectedData }]);
        }
        setRevenueHText(false);
        setImpDevicesHText(false);
        setRevenueRisk(selectedData.estimatedValue.toString());
        setImpDevices(selectedData.totalDeviceCount.toString());
        setAutoSuggestHText(false);

        setFilteredData([selectedData]);
        setSuggestionSelected(true);
        setSearchValue(
            selectedData.opportunityNumber +
                ' - ' +
                selectedData.accountName +
                ' - ' +
                selectedData.country +
                ' - ' +
                selectedData.topic?.substring(0, count)
        );
        setOppId(selectedData.opportunityId);
        setShowSuggestion(false);
    };

    const handleFocus = () => {
        if (searchValue.length > 2) {
            getCustomersList({searchValue: searchValue});
            setShowSuggestion(true);
        }
    };

    const suggestionRef = useRef<HTMLDivElement>(null);

    useSuggestionBoxDismiss(suggestionRef, setShowSuggestion);

    //loader shimmer element
    const shimmerElement = [
        { type: ShimmerElementType.line },
        { type: ShimmerElementType.gap, width: '5%' },
        { type: ShimmerElementType.line },
        { type: ShimmerElementType.gap, width: '5%' },
        { type: ShimmerElementType.line }
    ];

    const textInput = useRef(null);

    useEffect(() => {
        if (isFocus) {
            textInput.current.focus();
        }
    }, [isFocus]);

    return (
        <div className="search-MSX-cont" ref={suggestionRef} tabIndex={0} aria-label="Do you have an MSX opportunity ID">
            <TextField
                id='msx-search-field'
                className={`${autoSuggestHText ? 'error-feild' : ''}${showSuggestion ? 'active' : ''}`}
                value={searchValue}
                data-testid="search-box-test"
                onFocus={handleFocus}
                onChange={handleSearch}
                placeholder="e.g: 1-1KX7Y871 - Contoso Corporation - United States"
                autoComplete={'off'}
                componentRef={textInput}
                tabIndex={0}
                ariaLabel="Search by MSX Opportunity ID / Account ID / Customer Name"
            />
            {showSuggestion && (
                <div
                    className={`suggest-cont-box`}
                    aria-live="assertive"
                    aria-atomic="true">
                        {isLoading ? (
                            <div className="p-a-20">
                                <Shimmer className="p-b-5" shimmerElements={shimmerElement} />
                                <Shimmer className="p-b-5" shimmerElements={shimmerElement} />
                                <Shimmer shimmerElements={shimmerElement} />
                            </div>
                        ) : data?.length === 0 ? (
                            <div className="p-a-20" tabIndex={0} aria-label="Sorry, we could not find anything that matches your search">
                                <Text>
                                    <Trans>Sorry, we could not find anything that matches your search :(</Trans>
                                </Text>
                            </div>
                        ) : (
                            data?.map((item: MsxSuggestResponse) => (
                                <div
                                    key={item?.document?.id}
                                    tabIndex={0}
                                    onClick={() => handleSelectSuggestion(item?.document)}
                                    aria-label={`MSX Opportunity Suggestion: ${item?.document?.opportunityNumber} - ${item?.document?.accountName} - ${item?.document?.country}`}
                                    className="suggestion-item  p-t-10 p-b-10"
                                    onKeyPress={(e) => keyPress(e, () => handleSelectSuggestion(item?.document))}
                                    data-testid="select-suggest">
                                    <Text>
                                        {`${item?.document?.opportunityNumber} - ${item?.document?.accountName} - ${
                                            item?.document?.country
                                        }  - ${item?.document?.topic?.substring(0, count)} `}
                                    </Text>
                                </div>
                            ))
                        )}
                </div>
            )}
        </div>
    );
};
const useSuggestionBoxDismiss = (ref: React.RefObject<HTMLDivElement>, setShowSuggestion: Function) => {
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent | KeyboardEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setShowSuggestion(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('keypress', handleClickOutside, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keypress', handleClickOutside);
        };
    }, [setShowSuggestion, ref]);
};
